"use strict";
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var core_1 = require("@angular/core");
var router_1 = require("@angular/router");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var localize_router_parser_1 = require("./localize-router.parser");
var localize_router_config_1 = require("./localize-router.config");
/**
 * Localization service
 * modifyRoutes
 */
var LocalizeRouterService = /** @class */ (function () {
    /**
     * CTOR
     * @param parser
     * @param settings
     * @param router
     */
    function LocalizeRouterService(parser, settings, router) {
        this.parser = parser;
        this.settings = settings;
        this.router = router;
        this.routerEvents = new rxjs_1.Subject();
    }
    /**
     * Start up the service
     */
    LocalizeRouterService.prototype.init = function () {
        this.router.resetConfig(this.parser.routes);
        // subscribe to router events
        this.router.events
            .pipe(operators_1.filter(function (event) { return event instanceof router_1.NavigationStart; }), operators_1.pairwise())
            .subscribe(this._routeChanged());
    };
    /**
     * Change language and navigate to translated route
     * @param lang
     */
    LocalizeRouterService.prototype.changeLanguage = function (lang) {
        var _this = this;
        if (lang !== this.parser.currentLang) {
            var rootSnapshot_1 = this.router.routerState.snapshot.root;
            this.parser.translateRoutes(lang)
                .pipe(
            // set new routes to router
            operators_1.tap(function () { return _this.router.resetConfig(_this.parser.routes); }))
                .subscribe(function () {
                var urlSegments = _this.traverseSnapshot(rootSnapshot_1, true)
                    .filter(function (path, i) {
                    return !i || path; // filter out empty paths
                });
                var navigationExtras = __assign({}, rootSnapshot_1.queryParamMap.keys.length ? { queryParams: rootSnapshot_1.queryParams } : {}, rootSnapshot_1.fragment ? { fragment: rootSnapshot_1.fragment } : {});
                // use navigate to keep extras unchanged
                _this.router.navigate(urlSegments, navigationExtras);
            });
        }
    };
    /**
     * Traverses through the tree to assemble new translated url
     * @param snapshot
     * @param isRoot
     * @returns {string}
     */
    LocalizeRouterService.prototype.traverseSnapshot = function (snapshot, isRoot) {
        var _this = this;
        if (isRoot === void 0) { isRoot = false; }
        if (isRoot) {
            if (!snapshot.firstChild) {
                return [''];
            }
            if (this.settings.alwaysSetPrefix || this.parser.currentLang !== this.parser.defaultLang) {
                return ["/" + this.parser.currentLang].concat(this.traverseSnapshot(snapshot.firstChild.firstChild));
            }
            else {
                return this.traverseSnapshot(snapshot.firstChild.firstChild).slice();
            }
        }
        var urlPart = this.parseSegmentValue(snapshot);
        var outletChildren = snapshot.children
            .filter(function (child) { return child.outlet !== router_1.PRIMARY_OUTLET; });
        var outlets = outletChildren
            .reduce(function (acc, cur) {
            return ({
                outlets: __assign({}, acc.outlets, (_a = {}, _a[cur.outlet] = _this.parseSegmentValue(cur), _a))
            });
            var _a;
        }, { outlets: {} });
        var primaryChild = snapshot.children.find(function (child) { return child.outlet === router_1.PRIMARY_OUTLET; });
        return [
            urlPart
        ].concat(Object.keys(snapshot.params).length ? [snapshot.params] : [], outletChildren.length ? [outlets] : [], primaryChild ? this.traverseSnapshot(primaryChild) : []);
    };
    /**
     * Extracts new segment value based on routeConfig and url
     * @param snapshot
     * @returns {string}
     */
    LocalizeRouterService.prototype.parseSegmentValue = function (snapshot) {
        var _this = this;
        if (snapshot.routeConfig) {
            if (snapshot.routeConfig.path === '**') {
                return this.parser.translateRoute(snapshot.url
                    .filter(function (segment) { return segment.path; })
                    .map(function (segment) { return segment.path; })
                    .join('/'));
            }
            else if (snapshot.routeConfig.data) {
                var subPathSegments = snapshot.routeConfig.data.localizeRouter.path.split('/');
                return subPathSegments
                    .map(function (s, i) { return s.indexOf(':') === 0 ?
                    snapshot.url[i].path :
                    _this.parser.translateRoute(s); })
                    .join('/');
            }
        }
        return '';
    };
    /**
     * Translate route to current language
     * If new language is explicitly provided then replace language part in url with new language
     * @param path
     * @returns {string | any[]}
     */
    LocalizeRouterService.prototype.translateRoute = function (path) {
        var _this = this;
        // path is null (e.g. resetting auxiliary outlet)
        if (!path) {
            return path;
        }
        if (typeof path === 'string') {
            var url = this.parser.translateRoute(path);
            return !path.indexOf('/') ? "/" + this.parser.urlPrefix + url : url;
        }
        // it's an array
        var result = [];
        path.forEach(function (segment, index) {
            if (typeof segment === 'string') {
                var res = _this.parser.translateRoute(segment);
                if (!index && !segment.indexOf('/')) {
                    result.push("/" + _this.parser.urlPrefix + res);
                }
                else {
                    result.push(res);
                }
            }
            else {
                // translate router outlets block
                if (segment && segment.outlets) {
                    var outlets = {};
                    for (var key in segment.outlets) {
                        if (segment.outlets.hasOwnProperty(key)) {
                            outlets[key] = _this.translateRoute(segment.outlets[key]);
                        }
                    }
                    result.push(__assign({}, segment, { outlets: outlets }));
                }
                else {
                    result.push(segment);
                }
            }
        });
        return result;
    };
    /**
     * Event handler to react on route change
     * @returns {(event:any)=>void}
     * @private
     */
    LocalizeRouterService.prototype._routeChanged = function () {
        var _this = this;
        return function (_a) {
            var previousEvent = _a[0], currentEvent = _a[1];
            var previousLang = _this.parser.getLocationLang(previousEvent.url) || _this.parser.defaultLang;
            var currentLang = _this.parser.getLocationLang(currentEvent.url) || _this.parser.defaultLang;
            if (currentLang !== previousLang) {
                // mutate router config directly to avoid getting out of sync
                _this.parser.mutateRouterRootRoute(currentLang, previousLang, _this.router.config);
                _this.parser.translateRoutes(currentLang)
                    .pipe(
                // reset routes again once they are all translated
                operators_1.tap(function () { return _this.router.resetConfig(_this.parser.routes); }))
                    .subscribe(function () {
                    // Fire route change event
                    _this.routerEvents.next(currentLang);
                });
            }
        };
    };
    /** @nocollapse */
    LocalizeRouterService.ctorParameters = function () { return [
        { type: localize_router_parser_1.LocalizeParser, decorators: [{ type: core_1.Inject, args: [localize_router_parser_1.LocalizeParser,] }] },
        { type: localize_router_config_1.LocalizeRouterSettings, decorators: [{ type: core_1.Inject, args: [localize_router_config_1.LocalizeRouterSettings,] }] },
        { type: router_1.Router, decorators: [{ type: core_1.Inject, args: [router_1.Router,] }] }
    ]; };
    return LocalizeRouterService;
}());
exports.LocalizeRouterService = LocalizeRouterService;



//////////////////
// WEBPACK FOOTER
// ./node_modules/localize-router/src/localize-router.service.js
// module id = 24
// module chunks = 0